/**
 * Network Stats Display Component - V167.2ND
 *
 * Displays connections, matches, friends, and 2nd degree counts
 */
import React from 'react';
import { Users, Link, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';


interface NetworkStatsDisplayProps {
  connectionsCount: number;
  matchesCount: number;
  friendsCount: number;
  secondDegreeCount?: number; // V167.2ND: Optional 2nd degree count
  loading?: boolean;
  /** When true, keep showing last values while loading (stale-while-revalidate) */
  staleWhileRevalidate?: boolean;
  className?: string;
  onConnectionsClick?: () => void;
  onMatchesClick?: () => void;
  onFriendsClick?: () => void;
}

interface StatCardProps {
  icon: React.ReactNode;
  value: number;
  label: string;
  loading?: boolean;
  onClick?: () => void;
}

function StatCard({ icon, value, label, loading, onClick }: StatCardProps) {
  if (loading) {
    return (
      <div className="stat-card loading backdrop-blur-sm bg-[#F8F4F1] dark:bg-white/5 border border-[#E8E0DA]/50 dark:border-white/10 rounded-xl p-2 sm:p-3 text-center">
        <div className="skeleton-box bg-[#E8E0DA] dark:bg-white/10 rounded-full h-10 w-10 mx-auto mb-2" />
        <div className="skeleton-box bg-[#E8E0DA] dark:bg-white/10 rounded-lg h-6 w-12 mx-auto mb-1" />
        <div className="skeleton-box bg-[#E8E0DA] dark:bg-white/10 rounded-lg h-4 w-20 mx-auto" />
      </div>
    );
  }

  return (
    <div
      className="stat-card backdrop-blur-sm bg-white/80 dark:bg-white/5 border border-[#E8E0DA]/50 dark:border-white/10 rounded-xl p-2 sm:p-3 text-center shadow-[0_2px_16px_rgba(120,80,60,0.06)] dark:shadow-none cursor-pointer hover:bg-[#F8F4F1] dark:hover:bg-white/10 active:scale-95 transition-all"
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      <div className="stat-icon text-base sm:text-lg mb-1 sm:mb-2 flex items-center justify-center">
        {icon}
      </div>
      <div className="stat-value text-lg sm:text-2xl font-bold text-[#7C3AED] dark:text-white mb-1">{value}</div>
      <div className="stat-label text-[#6B6560] dark:text-white/70 text-[10px] font-medium tracking-wide uppercase text-center w-full leading-tight">{label}</div>
    </div>
  );
}

export function NetworkStatsDisplay({
  connectionsCount,
  matchesCount,
  friendsCount,
  secondDegreeCount = 0, // V167.2ND: Used to calculate total connections
  loading = false,
  staleWhileRevalidate = false,
  className = '',
  onConnectionsClick,
  onMatchesClick,
  onFriendsClick
}: NetworkStatsDisplayProps) {
  const { t } = useTranslation();

  // Rendering NetworkStatsDisplay

  // V167.2ND: Connections = Friends (1st degree) + 2nd Degree
  const totalConnections = friendsCount + secondDegreeCount;
  const showSkeleton = loading && !staleWhileRevalidate;

  const stats = [
    {
      icon: <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 dark:text-blue-300" aria-hidden="true" />,
      value: totalConnections,
      label: t('profile.connections'),
      onClick: onConnectionsClick,
    },
    {
      icon: <Link className="w-4 h-4 sm:w-5 sm:h-5 text-[#7C3AED] dark:text-[#A78BFA]" aria-hidden="true" />,
      value: matchesCount,
      label: t('profile.ties'),
      onClick: onMatchesClick,
    },
    {
      icon: <Star className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400 dark:text-amber-300 fill-current" aria-hidden="true" />,
      value: friendsCount,
      label: t('profile.friends'),
      onClick: onFriendsClick,
    },
  ];

  return (
    <div className={`network-stats-display grid grid-cols-3 gap-2 sm:gap-3 profile-stats-grid ${className}`}>
      {stats.map(stat => (
        <StatCard key={stat.label} {...stat} loading={showSkeleton} />
      ))}
    </div>
  );
}
